import React, { useEffect, useMemo, useState } from 'react';
import { p, tp, useP } from '../../i18n/useP';
import { useTranslation } from 'react-i18next';
import { usePersistentValue } from '../../hooks/usePersistentState';
import {
  listHrPeople, bulkUpdateHrPeople, listHrContracts,
  deleteHrPerson, updateHrPerson, onboardEmployee,
  createHrContract, updateHrContract, deleteHrContract,
  listPayrollMovements,
} from '../../api/hr';
import EditableCell from './EditableCell';
import ContractEditModal from './ContractEditModal';
import { getCompanyTimeYear } from '../../api/hrTime';
import { formatDate, formatNumber } from '../../utils/format';
import PageHeader from '../../components/PageHeader';
import CardShell from '../../components/CardShell';
import SearchField from '../../components/SearchField';
import SegmentedToggle from '../../components/SegmentedToggle';
import FilterIcon from '../../components/FilterIcon';
import FilterCount from '../../components/FilterCount';
import { useTableSort } from '../../hooks/useTableSort';
import { normalizeName, buildSearchKey } from '../../utils/personTable';
import './HrPage.css';

const GENDER_OPTIONS = [
  { value: 'M', label: 'M' },
  { value: 'F', label: 'F' },
];

/* ── Sortable column metadata ─────────────────────────────────────────────── */
const COLUMNS = [
  { key: 'first_name_sort',  label: tp('phrases:p_e4387ade0f'),           sortable: true,  type: 'text' },
  { key: 'last_name_sort',   label: p('Nom'),              sortable: true,  type: 'text' },
  { key: 'status_sort',      label: p('Statut'),           sortable: true,  type: 'text' },
  { key: 'gender',           label: p('Genre'),            sortable: true,  type: 'text' },
  { key: 'birth_date',       label: p('Date de naissance'), sortable: true, type: 'date' },
  { key: 'entry_date',       label: tp('phrases:p_74ec72cc90'),    sortable: true,  type: 'date' },
  { key: 'termination_date', label: p('Date de sortie'),   sortable: true,  type: 'date' },
  { key: 'department',       label: tp('phrases:p_3d7c87c2af'),       sortable: true,  type: 'text' },
];

// entry_date / termination_date sont dérivées des contrats → non éditables en masse.
const BULK_FIELDS = [
  { key: 'gender',           label: p('Genre') },
  { key: 'notes',            label: p('Notes') },
];

export default function HrPeoplePage({ activeTenant, setPage, year, embedded, openAdd, onAddConsumed }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id;
  const [rows, setRows] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const { sortCol, sortDir, handleSort: doSort, sortRows } = useTableSort(`hr_people_sort_${tenantId}`, 'last_name_sort');
  const [search, setSearch] = useState('');
  const [status, setStatus] = usePersistentValue(`hr_people_status_v2_${tenantId}`, 'all');
  const [selected, setSelected] = useState(() => new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [modalField, setModalField] = useState('gender');
  const [modalValue, setModalValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  // Ouverture du formulaire de création déclenchée depuis le menu d'import.
  // Requête consommée (réinitialisée) une fois ouverte → un remontage du panneau
  // (après un import) ne rouvre pas le formulaire par accident.
  useEffect(() => {
    if (openAdd) { setAddOpen(true); onAddConsumed?.(); }
  }, [openAdd]); // eslint-disable-line
  // Master-detail : lignes dépliées + édition d'un contrat.
  //   editingContract = null | { personId, contract: 'new' | <obj> }
  const [expanded, setExpanded] = useState(() => new Set());
  const [editingContract, setEditingContract] = useState(null);

  function refresh() {
    if (!tenantId) return;
    setLoading(true);
    Promise.all([listHrPeople(tenantId), listHrContracts(tenantId), listPayrollMovements(tenantId)])
      .then(([p, c, m]) => {
        if (!p.error) setRows(p.data || []);
        if (!c.error) setContracts(c.data || []);
        if (!m.error) setMovements(m.data || []);
      })
      .finally(() => setLoading(false));
  }
  useEffect(refresh, [tenantId]); // eslint-disable-line

  // Défaut vacances @100% pour l'onboarding — paramètre Company calendar.
  const [defaultVacationDays, setDefaultVacationDays] = useState(25);
  useEffect(() => {
    if (!tenantId || !year) return;
    getCompanyTimeYear(tenantId, year).then(r => {
      const v = r.error ? null : Number(r.data?.params?.vacation_days_full_time);
      setDefaultVacationDays(v > 0 ? v : 25);
    });
  }, [tenantId, year]);

  // Per-person status + department, derived from contracts.
  //   active : at least one open contract (end_date IS NULL)
  //   left   : no open contract — last_end_date = max(end_date)
  //   none   : no contract at all
  // Department = most recent contract by start_date (open ones win on ties).
  const personMeta = useMemo(() => {
    const meta = {};
    for (const c of contracts) {
      const pid = c.person_id;
      const cur = meta[pid] || { hasOpen: false, lastEnd: null, latestContract: null };
      if (c.end_date === null) cur.hasOpen = true;
      else if (!cur.lastEnd || c.end_date > cur.lastEnd) cur.lastEnd = c.end_date;
      if (!cur.latestContract || (c.start_date || '') > (cur.latestContract.start_date || '')) {
        cur.latestContract = c;
      }
      // Prefer an OPEN contract for department resolution
      if (c.end_date === null) {
        if (!cur.openContract || (c.start_date || '') > (cur.openContract.start_date || '')) {
          cur.openContract = c;
        }
      }
      meta[pid] = cur;
    }
    return meta;
  }, [contracts]);

  // Contrats par personne, plus récent d'abord — pour le panneau déplié.
  const contractsByPerson = useMemo(() => {
    const m = {};
    for (const c of contracts) (m[c.person_id] ||= []).push(c);
    for (const k of Object.keys(m)) {
      m[k].sort((a, b) => (b.start_date || '').localeCompare(a.start_date || ''));
    }
    return m;
  }, [contracts]);

  function toggleExpand(id) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function handleSort(col) {
    if (!COLUMNS.find(c => c.key === col && c.sortable)) return;
    doSort(col);
  }

  // enrich rows with derived fields used for sort + filter
  const enrichedRows = useMemo(() => rows.map(r => {
    const m = personMeta[r.id];
    const hasOpen = !!m?.hasOpen;
    const lastEnd = m?.lastEnd || null;
    const status = m == null ? 'none' : (hasOpen ? 'active' : 'left');
    // Sort key : active first, then left (by recency of departure), then none
    const status_sort =
      status === 'active' ? `0_${(r.last_name || '').toLowerCase()}` :
      status === 'left'   ? `1_${lastEnd || '9999'}` :
      `2_${(r.last_name || '').toLowerCase()}`;
    const openOrLatest = m?.openContract || m?.latestContract;
    return {
      ...r,
      department: openOrLatest?.department || '',
      first_name_sort: (r.first_name || '').toLowerCase(),
      last_name_sort:  (r.last_name  || '').toLowerCase(),
      _search: buildSearchKey(r.first_name, r.last_name, openOrLatest?.department),
      contract_status: status,
      last_contract_end: lastEnd,
      status_sort,
    };
  }), [rows, personMeta]);

  const filteredRows = useMemo(() => {
    let arr = enrichedRows;
    if (status === 'active') arr = arr.filter(r => r.contract_status === 'active');
    const q = normalizeName(search.trim());
    if (q) arr = arr.filter(r => r._search.includes(q));
    return arr;
  }, [enrichedRows, status, search]);

  const sortedRows = useMemo(
    () => sortRows(filteredRows),
    [filteredRows, sortCol, sortDir], // eslint-disable-line
  );

  const allSelected = sortedRows.length > 0 && selected.size === sortedRows.length;
  const someSelected = selected.size > 0 && !allSelected;

  function toggleRow(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleAll() {
    setSelected(prev => {
      if (prev.size === sortedRows.length) return new Set();
      return new Set(sortedRows.map(r => r.id));
    });
  }
  function clearSelection() {
    setSelected(new Set());
  }

  async function deleteSelected() {
    if (selected.size === 0 || submitting) return;
    const picked = sortedRows.filter(r => selected.has(r.id));
    const names = picked.map(r => `${r.first_name} ${r.last_name}`);
    const preview = names.slice(0, 8).join(', ') + (names.length > 8 ? `, … (+${names.length - 8})` : '');
    if (!window.confirm(
      p('Supprimer {{count}} collaborateur(s) ?\n{{preview}}\n\nLes contrats, compléments annuels et taux LPP seront supprimés en cascade. Action irréversible.', {
        count: selected.size,
        preview,
      }),
    )) return;
    setSubmitting(true);
    setError(null);
    try {
      for (const id of selected) {
        const ok = await deleteHrPerson(tenantId, id);
        if (ok.error) { setError(ok.error); break; }
      }
      clearSelection();
      refresh();
    } finally {
      setSubmitting(false);
    }
  }

  function openModal() {
    if (selected.size === 0) return;
    setModalField('gender');
    setModalValue('');
    setError(null);
    setModalOpen(true);
  }
  function closeModal() {
    setModalOpen(false);
    setError(null);
  }

  async function applyBulkUpdate() {
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const r = await bulkUpdateHrPeople(tenantId, {
        ids: [...selected],
        field: modalField,
        // Empty string → backend treats as null/clear for nullable fields.
        value: modalValue === '' ? null : modalValue,
      });
      if (r.error) {
        setError(r.error);
      } else {
        closeModal();
        clearSelection();
        refresh();
      }
    } catch (e) {
      setError(e.message || p('Erreur réseau'));
    } finally {
      setSubmitting(false);
    }
  }

  if (!tenantId) return <main className="hr-page"><div className="hr-page__empty">{p('Sélectionner une entreprise.')}</div></main>;

  /* Entête standard de carte (2026-09-01) : boutons en actions, filtres
     derrière l'entonnoir, recherche en dernier. */
  const nFilters = (status === 'active' ? 1 : 0) + (search.trim() ? 1 : 0);
  const stdFilters = (
    <>
      <FilterIcon />
      <SegmentedToggle
        compact
        label={p('Statut')} value={status} onChange={setStatus}
        options={[
          { value: 'all', label: p('Tous') },
          { value: 'active', label: p('Actifs'), tone: 'alert' },
        ]}
      />
      <FilterCount count={nFilters} onClear={() => { setStatus('all'); setSearch(''); }} />
    </>
  );
  const stdSearch = (
    <SearchField value={search} onChange={setSearch}
                 placeholder={t('phrases:p_224c3d8b39')} />
  );
  const controls = (
    <>
      {/* Ajouter un contrat depuis l'ENTÊTE (2026-08-27) : il fallait sinon
          déplier la bonne personne pour trouver le bouton. Sans personne
          imposée, la modale ouvre son sélecteur. */}
      <button className="hr-page__add-btn"
              onClick={() => setEditingContract({ personId: null, contract: 'new' })}
              disabled={rows.length === 0}>{p('+ Ajouter un contrat')}</button>
      <button className="hr-page__add-btn" onClick={() => setAddOpen(true)}>{p('+ Ajouter un collaborateur')}</button>
      <button
        className="hr-page__bulk-btn"
        onClick={openModal}
        disabled={selected.size === 0}
      >
        Édition groupée{selected.size > 0 && ` (${selected.size})`}
      </button>
      <button
        className="hr-page__del-btn"
        onClick={deleteSelected}
        disabled={selected.size === 0 || submitting}
        title={t('phrases:p_e2afbb3c2b')}
      >
        {t('common:actions.delete')}{selected.size > 0 && ` (${selected.size})`}
      </button>
    </>
  );

  return (
    <main className={`hr-page${embedded ? ' is-embedded' : ''}`}>
      {!embedded && (
        <PageHeader
          title={p('Collaborateurs')}
          actions={setPage && <button className="hr-page__back" onClick={() => setPage('home')}>{p('← Accueil')}</button>}
        />
      )}

      {loading && <div className="hr-page__loading">{t('common:status.loading')}</div>}
      {!loading && rows.length === 0 && <div className="hr-page__empty">{p('Aucun collaborateur pour cette entreprise.')}</div>}
      {!loading && rows.length > 0 && (
        <CardShell
          title={<>{p('Collaborateurs')} <small>· {filteredRows.length} {filteredRows.length > 1 ? p('collaborateurs') : p('collaborateur')}
            {rows.length !== filteredRows.length ? p(' · filtrés sur {{count}}', { count: rows.length }) : ''}
            {selected.size > 0 ? p(' · {{count}} sélectionné(s)', { count: selected.size }) : ''}</small></>}
          primaryAction={controls}
          filters={stdFilters}
          search={stdSearch}
          filterCount={nFilters}
          collapsible storageKey="hr-people"
          flush
        >
          <div className="hr-page__table-wrap">
            <table className="hr-page__table">
              <thead>
                <tr>
                  <th className="hr-page__check-col">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={el => { if (el) el.indeterminate = someSelected; }}
                      onChange={toggleAll}
                      title={allSelected ? p('Tout désélectionner') : p('Tout sélectionner')}
                    />
                  </th>
                  <th style={{ width: 28 }}></th>
                  {COLUMNS.map(col => {
                    const isSorted = sortCol === col.key;
                    return (
                      <th
                        key={col.key}
                        className={`hr-page__sort-th${isSorted ? ' is-sorted' : ''}`}
                        onClick={() => handleSort(col.key)}
                        title={col.sortable ? p('Cliquer pour trier') : ''}
                      >
                        <span>{col.label}</span>
                        <span className="hr-page__sort-ind">
                          {isSorted ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map(r => {
                  const isSel = selected.has(r.id);
                  const patch = async (payload) => {
                    const rr = await updateHrPerson(tenantId, r.id, payload);
                    if (rr.error) setError(rr.error);
                    // Optimistic local update — avoids full refresh that resets scroll.
                    else setRows(prev => prev.map(p2 => p2.id === r.id ? rr.data : p2));
                  };
                  const isExpanded = expanded.has(r.id);
                  const personContracts = contractsByPerson[r.id] || [];
                  return (
                    <React.Fragment key={r.id}>
                    <tr className={isSel ? 'is-selected' : ''}>
                      <td className="hr-page__check-col">
                        <input
                          type="checkbox"
                          checked={isSel}
                          onChange={() => toggleRow(r.id)}
                        />
                      </td>
                      <td style={{ textAlign: 'center', width: 28 }}>
                        <button
                          className={`hr-people__expand${isExpanded ? ' is-open' : ''}`}
                          onClick={() => toggleExpand(r.id)}
                          title={isExpanded ? p('Replier les contrats') : `${p('Voir les contrats')} (${personContracts.length})`}
                          aria-expanded={isExpanded}
                        >{isExpanded ? '−' : '+'}</button>
                      </td>
                      <td>
                        <EditableCell value={r.first_name} onCommit={v => patch({ first_name: v })} nullable={false} />
                      </td>
                      <td>
                        <EditableCell value={r.last_name} onCommit={v => patch({ last_name: v })} nullable={false}
                          formatter={v => v ? <strong>{String(v).toUpperCase()}</strong> : '—'} />
                      </td>
                      <td>
                        {r.contract_status === 'active' ? (
                          <span className="hr-status hr-status--active">{p('actif')}</span>
                        ) : r.contract_status === 'left' ? (
                          <span className="hr-status hr-status--left" title={`${p('Dernier contrat terminé le')} ${formatDate(r.last_contract_end)}`}>
                            {p('parti')} · {formatDate(r.last_contract_end)}
                          </span>
                        ) : (
                          <span className="hr-status hr-status--inactive">{p('sans contrat')}</span>
                        )}
                      </td>
                      <td>
                        <EditableCell value={r.gender} onCommit={v => patch({ gender: v })}
                          type="select" options={GENDER_OPTIONS} />
                      </td>
                      <td>
                        <EditableCell value={r.birth_date} onCommit={v => patch({ birth_date: v })} type="date" />
                      </td>
                      <td title={t('phrases:p_8c43b4b948')}>
                        {r.entry_date ? formatDate(r.entry_date) : '—'}
                      </td>
                      <td title={t('phrases:p_53528a365b')}
                          className={r.termination_date ? '' : 'hr-page__muted'}>
                        {r.termination_date ? formatDate(r.termination_date) : p('actif')}
                      </td>
                      <td className={r.department ? '' : 'hr-page__muted'}>{r.department || '—'}</td>
                    </tr>
                    {isExpanded && (
                      <tr className="hr-people__detail-row">
                        <td colSpan={2 + COLUMNS.length}>
                          <div className="hr-people__detail">
                            {personContracts.length === 0 ? (
                              <div className="hr-people__detail-empty">{p("Aucun contrat pour l'instant.")}</div>
                            ) : (
                              <table className="hr-people__detail-table">
                                <thead>
                                  <tr>
                                    <th style={{ width: 40 }}></th>
                                    <th className="hr-people__detail-th--lead">{p('Contrats')}</th>
                                    <th>{t('phrases:p_3d7c87c2af')}</th>
                                    <th>{t('phrases:p_f0955314c5')}</th>
                                    <th>{p('Fin')}</th>
                                    <th style={{ textAlign: 'right' }}>{p('Salaire / mois')}</th>
                                    <th style={{ textAlign: 'right' }}>{p('Nb')}</th>
                                    <th style={{ textAlign: 'right' }}>{p('Activité')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {personContracts.map(c => {
                                    const isOpen = c.end_date == null;
                                    return (
                                      <tr key={c.id}>
                                        <td style={{ textAlign: 'center' }}>
                                          <button
                                            className="hr-page__row-edit"
                                            onClick={() => setEditingContract({ personId: r.id, contract: c })}
                                            title={t('phrases:p_fe5a5d48ef')}
                                          >✎</button>
                                        </td>
                                        <td>{c.title || '—'}</td>
                                        <td className={c.department ? '' : 'hr-page__muted'}>{c.department || '—'}</td>
                                        <td>{c.start_date ? formatDate(c.start_date) : '—'}</td>
                                        <td className={isOpen ? 'hr-page__muted' : ''}>{isOpen ? p('en cours') : formatDate(c.end_date)}</td>
                                        <td style={{ textAlign: 'right' }}>{formatNumber(Number(c.monthly_salary || 0), 0)}</td>
                                        <td style={{ textAlign: 'right' }}>{c.nb_of_salary}</td>
                                        <td style={{ textAlign: 'right' }}>{formatNumber(Number(c.activity_rate || 0) * 100, 0)}%</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardShell>
      )}

      {modalOpen && (
        <BulkUpdateModal
          field={modalField}
          setField={setModalField}
          value={modalValue}
          setValue={setModalValue}
          count={selected.size}
          submitting={submitting}
          error={error}
          onCancel={closeModal}
          onApply={applyBulkUpdate}
        />
      )}

      {addOpen && (
        <OnboardModal
          defaultVacationDays={defaultVacationDays}
          onCancel={() => setAddOpen(false)}
          onSubmit={async (payload) => {
            const r = await onboardEmployee(tenantId, payload);
            if (r.error) { setError(r.error); return false; }
            setAddOpen(false);
            refresh();
            return true;
          }}
        />
      )}

      {editingContract && (
        <ContractEditModal
          mode={editingContract.contract === 'new' ? 'create' : 'edit'}
          defaultVacationDays={defaultVacationDays}
          people={rows}
          lockedPersonId={editingContract.personId || undefined}
          initial={editingContract.contract === 'new' ? null : editingContract.contract}
          movements={movements}
          contracts={contracts}
          onGoToPlanning={setPage ? () => setPage('hr-planning') : undefined}
          onCancel={() => setEditingContract(null)}
          onSubmit={async (payload, closing = []) => {
            // Clôture du (des) contrat(s) précédent(s) demandée par la modale :
            // appliquée AVANT la création, pour ne jamais laisser deux contrats
            // ouverts en base si la création échoue ensuite.
            for (const c of closing) {
              const rc = await updateHrContract(tenantId, c.id, { end_date: c.end_date });
              if (rc.error) { setError(rc.error); return false; }
            }
            const r = editingContract.contract === 'new'
              ? await createHrContract(tenantId, payload)
              : await updateHrContract(tenantId, editingContract.contract.id, payload);
            if (r.error) { setError(r.error); return false; }
            setEditingContract(null);
            refresh();
            return true;
          }}
          onDelete={editingContract.contract === 'new' ? undefined : async () => {
            if (!window.confirm(p('Supprimer ce contrat ?'))) return false;
            const ok = await deleteHrContract(tenantId, editingContract.contract.id);
            if (ok.error) { setError(ok.error); return false; }
            setEditingContract(null);
            refresh();
            return true;
          }}
          onDuplicate={editingContract.contract === 'new' ? undefined : async () => {
            const c = editingContract.contract;
            const today = new Date().toISOString().slice(0, 10);
            let newStart = today;
            if (c.end_date) {
              const d = new Date(c.end_date);
              d.setDate(d.getDate() + 1);
              newStart = d.toISOString().slice(0, 10);
            }
            const ok = await createHrContract(tenantId, {
              person_id: c.person_id,
              start_date: newStart,
              end_date: null,
              title: c.title,
              department: c.department,
              monthly_salary: c.monthly_salary,
              nb_of_salary: c.nb_of_salary,
              activity_rate: c.activity_rate,
              vacation_days: c.vacation_days,
              cs_empr_pct: c.cs_empr_pct,
              cs_empl_pct: c.cs_empl_pct,
            });
            if (ok.error) { setError(ok.error); return false; }
            setEditingContract(null);
            refresh();
            return true;
          }}
        />
      )}
    </main>
  );
}


/* ── Formulaire d'onboarding : Personne + Contrat + Année (extras + LPP) ── */
function OnboardModal({ onCancel, onSubmit, defaultVacationDays = 25 }) {
  const { t } = useTranslation();
  const p = useP();
  const currentYear = new Date().getFullYear();
  const defaultDate = `${currentYear}-01-01`;

  // Personne
  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [gender,    setGender]    = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [notes,     setNotes]     = useState('');
  // Contrat
  const [contractStart, setContractStart]   = useState(defaultDate);
  const [contractEnd,   setContractEnd]     = useState('');
  const [title,         setTitle]           = useState('');
  const [department,    setDepartment]      = useState('');
  const [monthlySalary, setMonthlySalary]   = useState('');
  const [nbOfSalary,    setNbOfSalary]      = useState('12');
  const [activityRate,  setActivityRate]    = useState('100');   // saisi en %
  const [vacationDays,  setVacationDays]    = useState(String(defaultVacationDays));
  // LPP — valeurs mensuelles. L'année est déduite de la date de début de contrat.
  const [lppEmprChf,    setLppEmprChf]      = useState('');   // CHF/mois
  const [lppEmplChf,    setLppEmplChf]      = useState('');   // CHF/mois
  const [lppExempt,     setLppExempt]       = useState(false);
  const lppYear = contractStart ? Number(contractStart.slice(0, 4)) : currentYear;

  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  async function submit() {
    if (!firstName.trim() || !lastName.trim() || !contractStart) {
      setErr(p('Prénom, nom et date de début de contrat sont requis.'));
      return;
    }
    if (!lppExempt && (!lppEmprChf || !lppEmplChf)) {
      setErr(p('LPP employeur et employé sont obligatoires — ou cochez « Pas de LPP ».'));
      return;
    }
    setSubmitting(true);
    setErr(null);
    const payload = {
      first_name: firstName.trim(),
      last_name:  lastName.trim(),
      gender:     gender || null,
      birth_date: birthDate || null,
      notes:      notes || null,

      contract_start_date: contractStart,
      contract_end_date:   contractEnd || null,
      title:               title || null,
      department:          department || null,
      monthly_salary:      monthlySalary || '0',
      nb_of_salary:        Number(nbOfSalary),
      activity_rate:       activityRate ? String(Number(activityRate) / 100) : '1.0',
      vacation_days:       Number(vacationDays),

      year:                lppYear,
      lpp_exempt:          lppExempt,
      lpp_employer_chf:    (!lppExempt && lppEmprChf) ? String(Number(lppEmprChf) * 12) : null,
      lpp_employee_chf:    (!lppExempt && lppEmplChf) ? String(Number(lppEmplChf) * 12) : null,
    };
    const ok = await onSubmit(payload);
    if (!ok) setSubmitting(false);
  }

  // Salaire de référence (info) pour permettre à l'utilisateur de cross-checker le CHF saisi
  // Âge (au jour de la saisie) déduit de la date de naissance.
  const age = (() => {
    if (!birthDate) return null;
    const b = new Date(birthDate), now = new Date();
    let a = now.getFullYear() - b.getFullYear();
    const m = now.getMonth() - b.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < b.getDate())) a -= 1;
    return a >= 0 && a < 120 ? a : null;
  })();
  const refSalary = (Number(monthlySalary) || 0) * (Number(nbOfSalary) || 0) * ((Number(activityRate) || 0) / 100);
  const lppEmprPctImplied = refSalary > 0 && Number(lppEmprChf) > 0 ? (Number(lppEmprChf) * 12 / refSalary * 100) : 0;
  const lppEmplPctImplied = refSalary > 0 && Number(lppEmplChf) > 0 ? (Number(lppEmplChf) * 12 / refSalary * 100) : 0;

  return (
    <div className="hr-modal__overlay">
      <div className="hr-modal hr-modal--wide" onClick={e => e.stopPropagation()}>
        <div className="hr-modal__header">
          <span className="hr-modal__title">{p('Ajouter un collaborateur')}</span>
          <button className="hr-modal__close" onClick={onCancel}>✕</button>
        </div>
        <div className="hr-modal__body">
          <div className="hr-onboard__section">
            <div className="hr-onboard__section-title">Personne</div>
            <div className="hr-onboard__grid">
              <Field label={t('phrases:p_72fe35057a')}><input className="hr-modal__input" type="text" value={firstName} onChange={e => setFirstName(e.target.value)} autoFocus /></Field>
              <Field label={p("Nom") + " *"}><input className="hr-modal__input" type="text" value={lastName} onChange={e => setLastName(e.target.value)} /></Field>
              <Field label={p('Genre')}>
                <select className="hr-modal__input" value={gender} onChange={e => setGender(e.target.value)}>
                  <option value="">—</option><option value="M">M</option><option value="F">F</option>
                </select>
              </Field>
              <Field label={p('Date de naissance')} hint={age != null ? `${age} ${p('ans')}` : ''}><input className="hr-modal__input" type="date" value={birthDate} onChange={e => setBirthDate(e.target.value)} /></Field>
              <Field label={p('Notes')}><input className="hr-modal__input" type="text" value={notes} onChange={e => setNotes(e.target.value)} /></Field>
            </div>
          </div>

          <div className="hr-onboard__section">
            <div className="hr-onboard__section-title">{p('Contrat')}</div>
            <div className="hr-onboard__grid">
              <Field label={t('phrases:p_2fbcb42f84')} hint={p("(= date d'entrée)")}><input className="hr-modal__input" type="date" value={contractStart} onChange={e => setContractStart(e.target.value)} /></Field>
              <Field label={p('Fin')} hint={p('(vide = ouvert)')}><input className="hr-modal__input" type="date" value={contractEnd} onChange={e => setContractEnd(e.target.value)} /></Field>
              <Field label={p('Titre')}><input className="hr-modal__input" type="text" value={title} onChange={e => setTitle(e.target.value)} /></Field>
              <Field label={t('phrases:p_3d7c87c2af')}><input className="hr-modal__input" type="text" value={department} onChange={e => setDepartment(e.target.value)} /></Field>
              <Field label={p('Salaire mensuel (CHF)')}><input className="hr-modal__input" type="number" step="1" value={monthlySalary} onChange={e => setMonthlySalary(e.target.value)} /></Field>
              <Field label={p('Nb de salaires')}>
                <select className="hr-modal__input" value={nbOfSalary} onChange={e => setNbOfSalary(e.target.value)}>
                  <option value="12">12</option><option value="13">13</option><option value="14">14</option>
                </select>
              </Field>
              <Field label={t('phrases:p_a48842269e')} hint="(%)"><input className="hr-modal__input" type="number" step="1" min="1" max="150" value={activityRate} onChange={e => setActivityRate(e.target.value)} /></Field>
              <Field label={p('Jours de vacances')}><input className="hr-modal__input" type="number" step="1" min="0" max="50" value={vacationDays} onChange={e => setVacationDays(e.target.value)} /></Field>
            </div>
          </div>

          <div className="hr-onboard__section">
            <div className="hr-onboard__section-title">{p('Données LPP')} {lppYear}</div>
            <label className="hr-onboard__exempt">
              <input type="checkbox" checked={lppExempt} onChange={e => setLppExempt(e.target.checked)} />
              {' '}{p('Pas de LPP (exonéré)')}
            </label>
            {!lppExempt && (
              <div className="hr-onboard__grid">
                <Field label={p('LPP employeur (CHF/mois) *')} hint={lppEmprPctImplied > 0 ? `≈ ${lppEmprPctImplied.toFixed(2)}${p('% du sal. réf.')}` : ''}>
                  <input className="hr-modal__input" type="number" step="1" value={lppEmprChf} onChange={e => setLppEmprChf(e.target.value)} />
                </Field>
                <Field label={t('phrases:p_ad21a1e7d6')} hint={lppEmplPctImplied > 0 ? `≈ ${lppEmplPctImplied.toFixed(2)}${p('% du sal. réf.')}` : ''}>
                  <input className="hr-modal__input" type="number" step="1" value={lppEmplChf} onChange={e => setLppEmplChf(e.target.value)} />
                </Field>
              </div>
            )}
          </div>

          {err && <div className="hr-modal__error">{err}</div>}
        </div>
        <div className="hr-modal__footer">
          <button className="hr-modal__btn" onClick={onCancel} disabled={submitting}>{t('common:actions.cancel')}</button>
          <button className="hr-modal__btn hr-modal__btn--primary" onClick={submit} disabled={submitting}>
            {submitting ? p('Création…') : p('Ajouter le collaborateur')}
          </button>
        </div>
      </div>
    </div>
  );
}


function Field({ label, hint, children }) {
  return (
    <div className="hr-modal__field">
      <label className="hr-modal__label">
        {label}
        {hint && <span className="hr-modal__hint">{hint}</span>}
      </label>
      {children}
    </div>
  );
}


/* ── Modal ────────────────────────────────────────────────────────────────── */
function BulkUpdateModal({ field, setField, value, setValue, count, submitting, error, onCancel, onApply }) {
  const { t } = useTranslation();
  const p = useP();
  // When the field changes, reset value to a sane default
  function changeField(f) {
    setField(f);
    setValue('');
  }

  const isNullable = field !== 'entry_date';

  function renderValueInput() {
    if (field === 'gender') {
      return (
        <select className="hr-modal__input" value={value} onChange={e => setValue(e.target.value)}>
          <option value="">{p('— (effacer)')}</option>
          <option value="M">M</option>
          <option value="F">F</option>
        </select>
      );
    }
    if (field === 'entry_date' || field === 'termination_date') {
      return (
        <input
          className="hr-modal__input"
          type="date"
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder="YYYY-MM-DD"
        />
      );
    }
    // notes
    return (
      <textarea
        className="hr-modal__input hr-modal__textarea"
        rows={3}
        value={value}
        onChange={e => setValue(e.target.value)}
        placeholder={p('(laisser vide pour effacer les notes)')}
      />
    );
  }

  return (
    <div className="hr-modal__overlay">
      <div className="hr-modal" onClick={e => e.stopPropagation()}>
        <div className="hr-modal__header">
          <span className="hr-modal__title">{p('Édition groupée')} — {count} {count > 1 ? p('collaborateurs') : p('collaborateur')}</span>
          <button className="hr-modal__close" onClick={onCancel}>✕</button>
        </div>

        <div className="hr-modal__body">
          <div className="hr-modal__field">
            <label className="hr-modal__label">{p('Champ')}</label>
            <select
              className="hr-modal__input"
              value={field}
              onChange={e => changeField(e.target.value)}
            >
              {BULK_FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
          </div>

          <div className="hr-modal__field">
            <label className="hr-modal__label">
              {p('Nouvelle valeur')}
              {isNullable && <span className="hr-modal__hint"> {p('(vide = effacer)')}</span>}
            </label>
            {renderValueInput()}
          </div>

          {error && <div className="hr-modal__error">{error}</div>}
        </div>

        <div className="hr-modal__footer">
          <button className="hr-modal__btn" onClick={onCancel} disabled={submitting}>{t('common:actions.cancel')}</button>
          <button
            className="hr-modal__btn hr-modal__btn--primary"
            onClick={onApply}
            disabled={submitting || (field === 'entry_date' && !value)}
          >
            {submitting ? p('Application…') : `${p('Appliquer à')} ${count}`}
          </button>
        </div>
      </div>
    </div>
  );
}
